'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

import {
	getLastViewedProducts,
	type LastViewedCacheProduct,
} from '../../utils/getLastViewedCache/getLastViewedCache'

export interface LastViewedContextType {
	lastViewedProducts: LastViewedCacheProduct[] | undefined
}

export const LastViewedContext = createContext<
	LastViewedContextType | undefined
>(undefined)

export interface LastViewedProviderProps {
	readonly colorId?: string
	readonly productId?: string
	readonly children: React.ReactNode
}

export function LastViewedProvider({
	colorId,
	productId,
	children,
}: LastViewedProviderProps): React.ReactNode {
	const searchParams = useSearchParams()
	const pathname = usePathname()

	const [lastViewedProducts, setLastViewedProducts] =
		useState<LastViewedCacheProduct[]>()

	const setLastViewed = useCallback(async () => {
		const newProducts = await getLastViewedProducts({
			productId,
			colorId,
		})

		setLastViewedProducts((prev) =>
			JSON.stringify(prev) !== JSON.stringify(newProducts) ? newProducts : prev
		)
	}, [productId, colorId])

	const prevPathname = useRef<string | null>(null)
	const prevSearchParams = useRef<string | null>(null)

	useEffect(() => {
		const currentSearchParams = searchParams.toString()

		if (
			prevPathname.current !== pathname ||
			prevSearchParams.current !== currentSearchParams
		) {
			setLastViewed().catch(console.error)
			prevPathname.current = pathname
			prevSearchParams.current = currentSearchParams
		}
	}, [pathname, searchParams, setLastViewed])

	const value = useMemo(
		() => ({
			lastViewedProducts,
		}),
		[lastViewedProducts]
	)

	return (
		<LastViewedContext.Provider value={value}>
			{children}
		</LastViewedContext.Provider>
	)
}

/** @throws {Error} If useLastViewed is used without LastViewedProvider */
export const useLastViewed = (): LastViewedContextType => {
	const context = useContext(LastViewedContext)
	if (!context) {
		throw new Error('useLastViewed must be used within a LastViewedProvider')
	}
	return context
}
